"use client";

import { useQuery } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { TestimonialCard } from "@/components/TestimonialCard";
import { ArticleCard } from "@/components/ArticleCard";
import { Button } from "@/components/ui/button";
import { Sparkles, MessageSquare, BookOpen, Megaphone } from "lucide-react";
import { useState } from "react";
import type { UpdateType } from "@/lib/types";

interface CommunityClientProps {
  initialPosts: any[];
}

export default function CommunityClient({ initialPosts }: CommunityClientProps) {
  const [filter, setFilter] = useState<UpdateType | 'all'>('all');
  
  // Hydrate with initial data, then listen for updates
  const posts = useQuery(api.community.getPublished) || initialPosts;

  const filteredPosts = posts?.filter((post: any) => 
    filter === 'all' ? true : post.type === filter
  );

  return (
    <div className="flex flex-col min-h-screen bg-background">
      <Header />
      <main className="flex-grow pt-32 pb-24">
        {/* Hero Section */}
        <section className="container mx-auto px-6 mb-20 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/5 text-primary font-black text-[10px] uppercase tracking-[0.2em] mb-8">
                <Sparkles className="w-3.5 h-3.5" />
                <span>Our Growing Community</span>
            </div>
            <h1 className="font-headline text-5xl md:text-7xl font-black text-foreground mb-6 tracking-tight">
                Stories, News & <br />
                <span className="text-accent italic font-normal">Expert Advice.</span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto font-medium">
                Welcome to our community hub. Read stories from our forever families, get the latest program updates, and learn how to give your puppy the best life possible.
            </p>
        </section>

        {/* Filters */}
        <section className="container mx-auto px-6 mb-16">
            <div className="flex flex-wrap justify-center gap-4">
                <Button 
                    variant={filter === 'all' ? 'default' : 'outline'}
                    onClick={() => setFilter('all')}
                    className="rounded-full px-8 h-12 font-bold"
                >
                    All Stories
                </Button>
                <Button 
                    variant={filter === 'testimonial' ? 'default' : 'outline'}
                    onClick={() => setFilter('testimonial')}
                    className="rounded-full px-8 h-12 font-bold flex gap-2"
                >
                    <MessageSquare className="w-4 h-4" /> Happy Tails
                </Button>
                <Button 
                    variant={filter === 'article' ? 'default' : 'outline'}
                    onClick={() => setFilter('article')}
                    className="rounded-full px-8 h-12 font-bold flex gap-2"
                >
                    <BookOpen className="w-4 h-4" /> Journal
                </Button>
                <Button 
                    variant={filter === 'announcement' ? 'default' : 'outline'}
                    onClick={() => setFilter('announcement')}
                    className="rounded-full px-8 h-12 font-bold flex gap-2"
                >
                    <Megaphone className="w-4 h-4" /> News
                </Button>
            </div>
        </section>

        {/* Content Grid */}
        <section className="container mx-auto px-6">
            {posts && filteredPosts.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
                    {filteredPosts.map((post: any) => (
                        post.type === 'testimonial' ? (
                            <TestimonialCard key={post._id} testimonial={post} />
                        ) : (
                            <ArticleCard key={post._id} article={post} />
                        )
                    ))}
                </div>
            ) : (
                <div className="text-center py-32 bg-secondary/20 rounded-[4rem] border-2 border-dashed border-primary/10">
                    <h2 className="text-2xl font-headline font-black text-foreground">No posts found</h2>
                    <p className="text-muted-foreground mt-2">Check back later for more updates!</p>
                </div>
            )}
        </section>
      </main>
      <Footer />
    </div>
  );
}
